import { DownOutlined, ExperimentOutlined } from '@ant-design/icons'

import { Form, Grid, Input, Modal, Select, Spin, message } from 'antd'

import { useEffect, useMemo, useState } from 'react'

import {
    apiGetTestCaseDetail,
    createTestCase,
    updateTestCase,
} from '../../../../../redux/testRun/testRun.service'

import {
    createFetchEpics,
    createFetchSprints,
    useAsyncSelect,
} from '../../../../../utils/asyncSelectHelpers'

import AsyncTagField from '../../../../shared/AsyncTagField'

import { resolveTestRunProjectId } from '../../../../shared/TestRunTabs'

import { isValidArray } from '../../../../../utils/helpers'

import styles from './AddTestCaseModal.module.css'

const { TextArea } = Input

function mergeSelectOptions(...groups) {
    const map = new Map()
    groups.flat().forEach((opt) => {
        if (opt?.value) map.set(opt.value, opt)
    })
    return Array.from(map.values())
}

// Used by the commented Epic field in the Organisation section.
// eslint-disable-next-line no-unused-vars
function renderEpicLabel(epicKey, title, styles) {
    return (
        <span className={styles.epicValue}>
            {epicKey ? (
                <span className={styles.epicValueKey}>{epicKey}</span>
            ) : null}
            <span className={styles.epicValueTitle}>{title || 'Epic'}</span>
        </span>
    )
}

/** Shared text rules: no spaces-only, no leading/trailing spaces */
function textFieldRules(fieldLabel, { required = false, max } = {}) {
    const rules = []

    if (required) {
        rules.push({
            required: true,
            message: `Please enter ${fieldLabel.toLowerCase()}`,
        })
    }

    rules.push({
        validator: (_, value) => {
            if (value == null || value === '') {
                return Promise.resolve()
            }
            if (typeof value !== 'string') {
                return Promise.resolve()
            }
            if (!value.trim()) {
                return Promise.reject(
                    new Error(
                        `${fieldLabel} cannot be only spaces. Please enter valid text.`
                    )
                )
            }
            if (value !== value.trim()) {
                return Promise.reject(
                    new Error(
                        `Please remove extra spaces at the start or end of ${fieldLabel.toLowerCase()}`
                    )
                )
            }
            return Promise.resolve()
        },
    })

    if (max != null) {
        rules.push({
            max,
            message: `${fieldLabel} must be ${max} characters or less`,
        })
    }

    return rules
}

const AddTestCaseModal = ({
    isOpen = false,
    setOpen = () => {},
    onSuccess = () => {},
    projectId: projectIdProp,
    testCaseId = null,
}) => {
    const [form] = Form.useForm()
    const screens = Grid.useBreakpoint()
    const modalWidth = screens.lg ? 780 : screens.md ? 680 : '94%'
    const isEditMode = Boolean(testCaseId)

    const [submitting, setSubmitting] = useState(false)
    const [detailLoading, setDetailLoading] = useState(false)
    const [sprintSeed, setSprintSeed] = useState([])
    const [epicSeed, setEpicSeed] = useState([])
    const [tagsOpen, setTagsOpen] = useState(false)

    const projectId = resolveTestRunProjectId(projectIdProp)
    const fetchSprintsForProject = useMemo(
        () => createFetchSprints(projectId),
        [projectId]
    )
    const fetchEpicsForProject = useMemo(
        () => createFetchEpics(projectId),
        [projectId]
    )
    const sprintSelect = useAsyncSelect(fetchSprintsForProject)
    const epicSelect = useAsyncSelect(fetchEpicsForProject)

    const sprintOptions = useMemo(
        () => mergeSelectOptions(sprintSeed, sprintSelect.options),
        [sprintSeed, sprintSelect.options]
    )
    const epicOptions = useMemo(
        () => mergeSelectOptions(epicSeed, epicSelect.options),
        [epicSeed, epicSelect.options]
    )
    const selectedEpicId = Form.useWatch('epic', form)
    // Used by the commented Epic field in the Organisation section.
    // eslint-disable-next-line no-unused-vars
    const selectedEpic = useMemo(() => {
        const id = selectedEpicId?.value || selectedEpicId
        if (!id) return null
        return epicOptions.find((opt) => opt.value === id) || null
    }, [selectedEpicId, epicOptions])

    useEffect(() => {
        if (!isOpen) {
            setTagsOpen(false)
            return
        }

        if (!isEditMode) {
            form.resetFields()
            setSprintSeed([])
            setEpicSeed([])
            setDetailLoading(false)
            return
        }

        let cancelled = false
        ;(async () => {
            setDetailLoading(true)
            form.resetFields()
            setSprintSeed([])
            setEpicSeed([])
            try {
                const res = await apiGetTestCaseDetail(testCaseId)
                const detail = res?.data
                if (cancelled) return

                if (!detail?._id) {
                    message.error('Failed to load test case details')

                    return
                }

                if (detail.sprint?._id) {
                    setSprintSeed([
                        {
                            value: detail.sprint._id,
                            label: detail.sprint.title || 'Sprint',
                        },
                    ])
                }

                if (detail.epic?._id) {
                    setEpicSeed([
                        {
                            value: detail.epic._id,
                            epicKey:
                                detail.epic.epicKey != null &&
                                detail.epic.epicKey !== ''
                                    ? String(detail.epic.epicKey)
                                    : '',
                            label: detail.epic.title || 'Epic',
                        },
                    ])
                }

                form.setFieldsValue({
                    title: detail.title || '',
                    description: detail.description || '',
                    preconditions: detail.preconditions || '',
                    testData: detail.testData || '',
                    steps: detail.steps || '',
                    expectedResult: detail.expectedResult || '',
                    sprint: detail.sprint?._id,
                    epic: detail.epic?._id,
                    tags: isValidArray(detail.tags)
                        ? detail.tags.map((tag) => ({
                              value: tag._id,
                              label: tag.name,
                          }))
                        : [],
                })
            } catch {
                if (!cancelled) {
                    message.error('Failed to load test case details')
                }
            } finally {
                if (!cancelled) setDetailLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [isOpen, isEditMode, testCaseId, form])

    const handleClose = () => {
        if (submitting) return

        form.resetFields()
        setSprintSeed([])
        setEpicSeed([])
        setOpen(false)
    }

    const buildPayloadFields = (values) => {
        const sprintId = values?.sprint?.value || values?.sprint
        const epicId = values?.epic?.value || values?.epic || null
        const tagIds =
            values?.tags?.map((t) => t?.value || t).filter(Boolean) || []

        const clean = (v) =>
            typeof v === 'string' ? v.trim() : v == null ? '' : String(v)

        return {
            title: clean(values.title),
            description: clean(values.description),
            preconditions: clean(values.preconditions),
            testData: clean(values.testData),
            steps: clean(values.steps),
            expectedResult: clean(values.expectedResult),
            sprint: sprintId,
            epic: epicId,
            tags: tagIds,
        }
    }

    const handleSubmit = async (values) => {
        if (!projectId) {
            message.error('Project is required')

            return
        }

        setSubmitting(true)

        try {
            const fields = buildPayloadFields(values)

            if (isEditMode) {
                const res = await updateTestCase({
                    id: testCaseId,

                    data: fields,
                })

                if (res?.data?.success) {
                    message.success(
                        res?.data?.message || 'Test case updated successfully'
                    )

                    handleClose()

                    onSuccess()
                } else {
                    message.error(
                        res?.data?.message || 'Failed to update test case'
                    )
                }

                return
            }

            const res = await createTestCase({
                ...fields,

                project: projectId,
            })

            if (res?.data?.success) {
                message.success('Test case added successfully')

                handleClose()

                onSuccess()
            } else {
                message.error(res?.data?.message || 'Failed to add test case')
            }
        } catch {
            message.error(
                isEditMode
                    ? 'Failed to update test case'
                    : 'Failed to add test case'
            )
        } finally {
            setSubmitting(false)
        }
    }

    const selectSuffix = (
        <DownOutlined style={{ fontSize: 11, color: '#64748b' }} />
    )

    return (
        <Modal
            className={styles.modal}
            open={isOpen}
            onCancel={handleClose}
            destroyOnClose
            width={modalWidth}
            centered
            maskClosable={!submitting && !detailLoading}
            closable={!submitting && !detailLoading}
            title={
                <div className={styles.header}>
                    <div className={styles.headerIcon}>
                        <ExperimentOutlined />
                    </div>

                    <div className={styles.headerText}>
                        <h2 className={styles.modalTitle}>
                            {isEditMode ? 'Edit Test Case' : 'Add Test Case'}
                        </h2>

                        <p className={styles.subtitle}>
                            {isEditMode
                                ? 'Update the test scenario, steps, and organisation for this case.'
                                : 'Define the test scenario, steps, and link it to a sprint before adding it to your project.'}
                        </p>
                    </div>
                </div>
            }
            footer={
                <div className={styles.footerBar}>
                    <span className={styles.footerHint}>
                        Title, tags, and sprint are required
                    </span>

                    <div className={styles.footerActions}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={handleClose}
                            disabled={submitting || detailLoading}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            disabled={submitting || detailLoading}
                            onClick={() => form.submit()}
                            className={`${styles.submitButton} ${
                                submitting ? styles.submitButtonDisabled : ''
                            }`}
                        >
                            {submitting
                                ? isEditMode
                                    ? 'Saving...'
                                    : 'Adding...'
                                : isEditMode
                                  ? 'Save Changes'
                                  : 'Add Test Case'}
                        </button>
                    </div>
                </div>
            }
        >
            {detailLoading ? (
                <div className={styles.loadingWrap}>
                    <Spin />
                </div>
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    className={styles.form}
                    onFinish={handleSubmit}
                    requiredMark={false}
                    initialValues={{ tags: [] }}
                >
                    <div className={styles.sections}>
                        <section className={styles.sectionCard}>
                            <div className={styles.sectionCardHeader}>
                                <p className={styles.sectionEyebrow}>Step 1</p>

                                <h3 className={styles.sectionTitle}>
                                    Overview
                                </h3>
                            </div>

                            <div className={styles.formGrid}>
                                <Form.Item
                                    label="Title"
                                    name="title"
                                    className={styles.formGridFull}
                                    rules={textFieldRules('Title', {
                                        required: true,
                                        max: 200,
                                    })}
                                >
                                    <Input
                                        className={styles.fieldControl}
                                        placeholder="e.g. Verify login with valid credentials"
                                        allowClear
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Description"
                                    name="description"
                                    className={styles.formGridFull}
                                    rules={textFieldRules('Description')}
                                >
                                    <TextArea
                                        className={styles.fieldTextarea}
                                        autoSize={{ minRows: 2, maxRows: 12 }}
                                        placeholder="Brief summary of what this test case covers…"
                                    />
                                </Form.Item>
                            </div>
                        </section>

                        <section className={styles.sectionCard}>
                            <div className={styles.sectionCardHeader}>
                                <p className={styles.sectionEyebrow}>Step 2</p>

                                <h3 className={styles.sectionTitle}>
                                    Test specification
                                </h3>
                            </div>

                            <div className={styles.formGrid}>
                                <Form.Item
                                    label="Preconditions"
                                    name="preconditions"
                                    className={styles.formGridFull}
                                    rules={textFieldRules('Preconditions')}
                                >
                                    <TextArea
                                        className={styles.fieldTextarea}
                                        autoSize={{ minRows: 2, maxRows: 12 }}
                                        placeholder="What must be true before running this test?"
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Test data"
                                    name="testData"
                                    rules={textFieldRules('Test data')}
                                >
                                    <TextArea
                                        className={styles.fieldTextarea}
                                        autoSize={{ minRows: 2, maxRows: 10 }}
                                        placeholder="Sample inputs or test data"
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Expected result"
                                    name="expectedResult"
                                    rules={textFieldRules('Expected result')}
                                >
                                    <TextArea
                                        className={styles.fieldTextarea}
                                        autoSize={{ minRows: 2, maxRows: 10 }}
                                        placeholder="What should happen when the test passes?"
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Steps"
                                    name="steps"
                                    className={styles.formGridFull}
                                    rules={textFieldRules('Steps')}
                                >
                                    <TextArea
                                        className={styles.fieldTextarea}
                                        autoSize={{ minRows: 2, maxRows: 14 }}
                                        placeholder={
                                            '1. Open app\n2. Enter credentials\n3. Click login'
                                        }
                                    />
                                </Form.Item>
                            </div>
                        </section>

                        <section className={styles.sectionCard}>
                            <div className={styles.sectionCardHeader}>
                                <p className={styles.sectionEyebrow}>Step 3</p>

                                <h3 className={styles.sectionTitle}>
                                    Organisation
                                </h3>
                            </div>

                            <div className={styles.formGrid}>
                                <Form.Item
                                    label="Sprint"
                                    name="sprint"
                                    className={styles.formGridFull}
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select a sprint',
                                        },
                                    ]}
                                >
                                    <Select
                                        allowClear
                                        showSearch
                                        autoComplete="off"
                                        placeholder="Select sprint"
                                        filterOption={false}
                                        searchValue={sprintSelect.searchValue}
                                        autoClearSearchValue={false}
                                        popupMatchSelectWidth
                                        className={styles.sprintSelect}
                                        popupClassName={
                                            styles.sprintSelectDropdown
                                        }
                                        suffixIcon={selectSuffix}
                                        getPopupContainer={(trigger) =>
                                            trigger.closest(
                                                '.ant-modal-body'
                                            ) ||
                                            trigger.parentElement ||
                                            document.body
                                        }
                                        onSearch={sprintSelect.onSearch}
                                        onOpenChange={(open) => {
                                            sprintSelect.onDropdownVisibleChange(
                                                open
                                            )
                                            if (open) setTagsOpen(false)
                                        }}
                                        onSelect={() => {
                                            sprintSelect.onSelect()
                                            setTagsOpen(false)
                                        }}
                                        onChange={() => {
                                            sprintSelect.onSelect()
                                            setTagsOpen(false)
                                        }}
                                        onClear={sprintSelect.onClear}
                                        onPopupScroll={
                                            sprintSelect.onPopupScroll
                                        }
                                        options={sprintOptions}
                                        loading={sprintSelect.loading}
                                        notFoundContent={
                                            sprintSelect.notFoundContent
                                        }
                                    />
                                </Form.Item>

                                {/* Epic field hidden for now
                                <Form.Item label="Epic" name="epic">
                                    <Select
                                        allowClear
                                        showSearch
                                        autoComplete="off"
                                        placeholder="Select epic"
                                        filterOption={false}
                                        popupMatchSelectWidth
                                        className={styles.sprintSelect}
                                        popupClassName={
                                            styles.sprintSelectDropdown
                                        }
                                        suffixIcon={selectSuffix}
                                        getPopupContainer={(trigger) =>
                                            trigger.closest(
                                                '.ant-modal-body'
                                            ) ||
                                            trigger.parentElement ||
                                            document.body
                                        }
                                        onSearch={epicSelect.onSearch}
                                        onOpenChange={
                                            epicSelect.onDropdownVisibleChange
                                        }
                                        onPopupScroll={
                                            epicSelect.onPopupScroll
                                        }
                                        options={epicOptions}
                                        loading={epicSelect.loading}
                                        notFoundContent={
                                            epicSelect.notFoundContent
                                        }
                                        optionRender={(option) =>
                                            renderEpicLabel(
                                                option.data?.epicKey,
                                                option.data?.label ??
                                                    option.label,
                                                styles
                                            )
                                        }
                                        labelRender={() =>
                                            selectedEpic
                                                ? renderEpicLabel(
                                                      selectedEpic.epicKey,
                                                      selectedEpic.label,
                                                      styles
                                                  )
                                                : undefined
                                        }
                                    />
                                </Form.Item>
                                */}

                                <div className={styles.formGridFull}>
                                    <AsyncTagField
                                        form={form}
                                        projectId={projectId}
                                        creatable
                                        maxTagCount={5}
                                        selectClassName="pool-tag-select"
                                        placeholder="Select tags"
                                        open={tagsOpen}
                                        onOpenChange={setTagsOpen}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please select at least one tag',
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        </section>
                    </div>
                </Form>
            )}
        </Modal>
    )
}

export default AddTestCaseModal
